/**
 * AI CONTEXT: legal-compliance - Centralized legal documents and sweepstakes rules
 * 
 * This page includes:
 * - Terms & Conditions, Privacy Policy, Sweepstakes Rules
 * - Age verification anchor and guidance
 * - Consistent dark theme and accessible typography
 * 
 * Architecture decisions:
 * - Client-side anchors for quick navigation
 * - Structured headings for readability
 * - Keep content static until CMS is introduced
 * 
 * Future considerations:
 * - Drive content from CMS
 * - Add print/export support
 * - Add jurisdiction-specific sections if needed
 */

import React from 'react';
import { Shield, FileText, Scale } from 'lucide-react';

const Legal = () => {
  return (
    <div className="min-h-screen bg-theme-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-theme-silver mb-4">
            Legal Information
          </h1>
          <p className="text-lg text-theme-silver-light">
            Important legal information and policies for Tolbert Tactical
          </p>
        </div>

        {/* Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            <a href="#terms" className="btn-secondary">Terms & Conditions</a>
            <a href="#privacy" className="btn-secondary">Privacy Policy</a>
            <a href="#sweepstakes" className="btn-secondary">Sweepstakes Rules</a>
            <a href="#age-verification" className="btn-secondary">Age Verification</a>
          </div>
        </div>

        {/* Terms & Conditions */}
        <section id="terms" className="glass-card-dark mb-8 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <FileText className="h-6 w-6 text-custom-orange" />
            <h2 className="text-2xl font-bold text-theme-silver">Terms & Conditions</h2>
          </div>
          
          <div className="space-y-4 text-theme-silver-light">
            <p>
              By using Tolbert Tactical's website and services, you agree to these terms and conditions.
            </p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">1. Eligibility</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You must be 21 years or older to participate in giveaways and sweepstakes</li>
              <li>You must be a legal resident of the United States</li>
              <li>You must have a valid email address and phone number</li>
              <li>You must not be prohibited by federal, state, or local law from owning or possessing a firearm</li>
              <li>Employees, contractors, affiliates, and immediate family members of Tolbert Tactical LLC are not eligible</li>
              <li>Participation is void in states where prohibited by law</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">2. Account Registration</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining account security</li>
              <li>One account per person is allowed</li>
              <li>We reserve the right to terminate accounts for violations</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">3. Purchases & Credits</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>All purchases are final and non-refundable</li>
              <li>Product purchases earn credits for giveaway entry</li>
              <li>Credits are tier-specific and can only be used for giveaways of the same tier</li>
              <li>Free users must purchase products to earn credits for giveaway entry</li>
              <li>Subscribers get automatic credits and early access to giveaways</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">4. SMS Notifications</h3>
            <p>Customers who opt in to SMS notifications agree to receive messages related to their Tolbert Tactical account and purchases, including order confirmations, shipping updates, cart reminders, product alerts, and giveaway notifications. Message frequency may vary; message and data rates may apply. You may opt out at any time by replying STOP, or request help by replying HELP.</p>
          </div>
        </section>

        {/* Privacy Policy */}
        <section id="privacy" className="glass-card-dark mb-8 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="h-6 w-6 text-custom-orange" />
            <h2 className="text-2xl font-bold text-theme-silver">Privacy Policy</h2>
          </div>
          
          <div className="space-y-4 text-theme-silver-light">
            <p>
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">Information We Collect</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Name, email address, and phone number</li>
              <li>Billing and shipping addresses</li>
              <li>Purchase history and giveaway entries</li>
              <li>Credit balance and subscription status</li>
              <li>Website usage data and cookies</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">How We Use Your Information</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Process orders and manage your account</li>
              <li>Track credits and manage giveaway entries</li>
              <li>Administer giveaways and select winners</li>
              <li>Send important updates and notifications</li>
              <li>Improve our services and user experience</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">Data Protection</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We use industry-standard encryption</li>
              <li>We never sell your personal information</li>
              <li>We limit access to authorized personnel only</li>
              <li>We retain data only as long as necessary</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">SMS Policy</h3>
            <p>By providing your mobile number and opting in, you consent to receive SMS messages from Tolbert Tactical. Messages may include order confirmations, shipping updates, product alerts, cart reminders, and giveaway notifications tied to your purchases. Message frequency: up to 4 messages per month. Message and data rates may apply. Reply STOP to unsubscribe at any time. Reply HELP for customer support. We do not sell, rent, or share your mobile information with third parties. Consent is not required as a condition of purchase.</p>
          </div>
        </section>

        {/* Sweepstakes Rules */}
        <section id="sweepstakes" className="glass-card-dark mb-8 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Scale className="h-6 w-6 text-custom-orange" />
            <h2 className="text-2xl font-bold text-theme-silver">Tolbert Tactical Sweepstakes Rules & Conditions</h2>
          </div>
          
          <div className="space-y-4 text-theme-silver-light">
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
              <p className="text-red-200 font-semibold text-center">
                VOID WHERE PROHIBITED OR RESTRICTED BY LAW. MUST BE 21+. FIREARM WINNERS MUST PASS BACKGROUND CHECK.
              </p>
            </div>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">1. Sponsor</h3>
            <p>This Sweepstakes is sponsored by Tolbert Tactical LLC ("Sponsor").</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">2. Eligibility</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Must be 21 years of age or older at the time of entry.</li>
              <li>Must be a legal resident of the United States.</li>
              <li>Sweepstakes is void in states where prohibited, including but not limited to: California, New York, Rhode Island, Washington D.C., Puerto Rico, Guam, and any U.S. territories.</li>
              <li>Certain prizes (including specific firearms, magazines, or accessories) may not be legal in all states. If a winner resides in a state where the prize is prohibited, the prize will be forfeited and an alternate winner will be selected.</li>
              <li>Must not be prohibited by federal, state, or local law from owning or possessing a firearm.</li>
              <li>Employees, contractors, affiliates, and immediate family members of Sponsor are not eligible.</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">3. Sweepstakes Period</h3>
            <p>Each giveaway will clearly display its official start date, end date, and closing time (MST/MDT). All entries must be received within that stated period to be eligible.</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">4. How to Enter</h3>
            <p>There are two ways to enter:</p>
            
            <h4 className="text-md font-semibold text-theme-silver mt-4 mb-2">(A) Purchase Entry</h4>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Eligible entrants may receive entries by purchasing goods from Sponsor during the Sweepstakes Period.</li>
              <li>Each dollar spent (excluding shipping/taxes) equals the designated number of entries as described on the product listing.</li>
            </ul>
            
            <h4 className="text-md font-semibold text-theme-silver mt-4 mb-2">(B) Free Mail-In Entry</h4>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>To enter without making a purchase, handprint your name, complete address, email, phone number, date of birth, and the exact Sweepstakes name on a 3x5 card and mail it in a stamped envelope to:</li>
            </ul>
            <div className="bg-theme-bronze/10 border border-theme-bronze/30 rounded-lg p-4 ml-4 mt-2">
              <p className="font-semibold text-theme-silver">Tolbert Tactical LLC – [Sweepstakes Name] Entry</p>
              <p>PO Box 910685</p>
              <p>St. George, UT 84791</p>
            </div>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Each envelope must contain only one entry. Multiple entries per envelope will be void.</li>
              <li>Mail-in entries must be postmarked by the end of the Sweepstakes Period and received within 7 days after the closing date.</li>
              <li>Each mail-in entry equals the same number of entries as the smallest purchase option.</li>
            </ul>
            
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mt-4">
              <p className="text-green-200 font-semibold text-center">
                NO PURCHASE NECESSARY TO ENTER OR WIN. A PURCHASE OR PAYMENT WILL NOT INCREASE YOUR CHANCES OF WINNING.
              </p>
            </div>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">5. Prize & Value</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Each giveaway will clearly display its prize description (e.g., rifle make/model) and its Approximate Retail Value (ARV).</li>
              <li>The Prize is non-transferable, and no substitution or cash alternative is permitted except at Sponsor's sole discretion.</li>
              <li>Firearm prizes will be transferred through a Federal Firearms Licensed (FFL) dealer only. Winner must comply with all applicable federal, state, and local laws, including passing a background check. If the winner is unable to legally take possession, the Prize will be forfeited and another winner may be selected.</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">6. Odds of Winning</h3>
            <p>The odds of winning depend on the total number of eligible entries received (purchased + mail-in).</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">7. Winner Selection & Notification</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Winner will be selected in a random drawing conducted by Sponsor within 7 days after the Sweepstakes ends.</li>
              <li>Winner will be notified by email and/or phone within 72 hours. Winner must respond within 7 days to claim the Prize or it will be forfeited and an alternate winner will be drawn.</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">8. Verification of Winner</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Winner must provide valid government-issued photo ID and complete any required FFL transfer paperwork.</li>
              <li>Winner must be 21+ and pass all applicable federal and state background checks.</li>
              <li>Failure to meet eligibility or legal requirements will result in prize forfeiture.</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">9. Taxes</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Winner is solely responsible for any and all federal, state, and local taxes associated with acceptance of the Prize.</li>
              <li>If required by law, Sponsor will issue an IRS Form 1099-MISC reflecting the ARV of the Prize.</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">10. Compliance with Laws</h3>
            <p>Entrants are solely responsible for ensuring their participation complies with all applicable federal, state, and local laws.</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">11. Release of Liability</h3>
            <p>By entering, participants release Sponsor, its affiliates, partners, advertising/marketing agencies, and each of their respective officers, directors, employees, and agents from any liability, claims, or damages arising out of participation or the Prize.</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">12. Modification or Cancellation</h3>
            <p>Sponsor may, in its sole discretion, cancel, terminate, modify, or suspend the Sweepstakes if it cannot run as planned due to technical issues, tampering, fraud, force majeure events, or any other cause affecting the integrity of the Sweepstakes.</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">13. Publicity</h3>
            <p>Except where prohibited by law, entry into the Sweepstakes constitutes consent for Sponsor to use the winner's name, likeness, and city/state of residence for advertising and promotional purposes without additional compensation.</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">14. Privacy</h3>
            <p>Information submitted by entrants will be used in accordance with Sponsor's Privacy Policy. Personal data will not be sold to third parties.</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">15. Governing Law</h3>
            <p>This Sweepstakes is governed by the laws of the State of Utah, without regard to conflict of law principles.</p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">16. Sponsor's Decisions Final</h3>
            <p>All decisions of Sponsor regarding the Sweepstakes are final and binding.</p>
          </div>
        </section>

        {/* Age Verification */}
        <section id="age-verification" className="glass-card-dark mb-8 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className="h-6 w-6 text-custom-orange" />
            <h2 className="text-2xl font-bold text-theme-silver">Age Verification</h2>
          </div>
          
          <div className="space-y-4 text-theme-silver-light">
            <p>
              Age verification is required for legal compliance and safety.
            </p>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">Legal Requirements</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You must be 21 years or older to access this website</li>
              <li>Age verification is required by federal and state laws</li>
              <li>False information may result in account termination</li>
              <li>We may request additional verification documents</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">Verification Process</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Initial verification through website interface</li>
              <li>Additional verification may be required for purchases</li>
              <li>Government-issued ID may be requested</li>
              <li>Verification is required for giveaway participation</li>
            </ul>
            
            <h3 className="text-lg font-semibold text-theme-silver mt-6 mb-3">Compliance</h3>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We comply with all applicable age verification laws</li>
              <li>We maintain records as required by law</li>
              <li>We cooperate with law enforcement when required</li>
              <li>We regularly review and update our verification procedures</li>
            </ul>
          </div>
        </section>

        {/* Contact Information */}
        <section className="glass-card-dark p-6">
          <h2 className="text-2xl font-bold text-theme-silver mb-4">Contact Us</h2>
          <div className="space-y-4 text-theme-silver-light">
            <p>
              If you have questions about our legal policies, please contact us:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-theme-silver mb-2">Email</h3>
                <p>legal@tolberttactical.com</p>
              </div>
              <div>
                <h3 className="font-semibold text-theme-silver mb-2">Phone</h3>
                <p>1-800-TOLBERT</p>
              </div>
              <div>
                <h3 className="font-semibold text-theme-silver mb-2">Address</h3>
                <p>Tolbert Tactical LLC<br />PO Box 910685<br />St. George, UT 84791</p>
              </div>
              <div>
                <h3 className="font-semibold text-theme-silver mb-2">Hours</h3>
                <p>Monday - Friday<br />9:00 AM - 5:00 PM EST</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Legal; 